import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import { safeStorage } from '@utils/storage'
import { logAddToCart, logRecoveredCartFailure, logRecoveredCartSuccess, logRemoveFromCart } from '@utils/analytics'
import type { ShippingType } from '@services/client/checkout/useGetShippingMethods'
import type { CouponData } from 'types/payment'
import { createCart } from '@services/client/cart/createCart'
import { getCurrentCart } from '@services/client/cart/getCurrentCart'
import { getCartByHash } from '@services/client/cart/getCartByHash'
import { getPatientsList } from '@services/client/patient/getPatientsList'
import { enrichCartItemsWithCategories, needsEnrichment } from '@services/client/cart/enrichCartItemsWithCategories'
import { resolveParentHashItems } from '@services/client/cart/resolveParentHashItems'
import { toast } from 'react-toastify'
import { useAuthState } from './auth'
import type { RegisterForm } from 'app/connect/[vendor]/checkout/Checkout'
import http from '@services/HTTP'
import {
  cartHasInvestigation,
  cartHasMultiProduct,
  cartHasSubscription,
  getMixedCartConflict,
  getMixedCartMessage,
  resolveSingleItemCart,
  SINGLE_ITEM_CART_TRIMMED_MESSAGE
} from '@utils/mixedCart'
import { isCouponRefusal } from '@utils/coupon'
import type { ProductGroup, UpsellModalConfig } from 'types/product'
import type { iVariation } from 'types/variation'

export interface CartItem {
  id: string
  image: string
  parentId: string
  title: string
  price: number
  quantity: number
  patient?: string
  answer?: string
  /** Consultation upload s3 keys snapshotted at add-to-cart; claimed for `patient` on order success. */
  fileKeys?: string[]
  is_valid?: boolean
  questionnaire?: iQuestion[]
  questionnaire_hash?: string
  categories?: string[]
  product_group?: ProductGroup
  /** Recurring terms for products with no pa_ep_frequency attribute. Copied off the
   * variant at add time — `variation` only carries pa_ep_* keys, so it can't be re-derived. */
  subscription?: iVariation['subscription']
  addLogged?: boolean
  /** Set while the item came from a public reorder payload and the logged-in user has not been confirmed as the order's owner. */
  reorderHash?: string
  /** Set while the item came from a public cart-recovery link and its patient has not been checked against the logged-in user. */
  recoveryCartHash?: string
  variation?: {
    attribute: string
    value: string
    label: string
  }[]
}

/** The product whose add to cart opens the upsell modal. */
export interface UpsellTrigger {
  hash: string
  title: string
  modal: UpsellModalConfig
}

interface CheckoutStore {
  coupon: CouponData | null
  setCoupon: (coupon: CouponData | null) => void
  revalidateCoupon: () => Promise<void>
  rated: string[]
  setRated: (orderHash: string) => void
  cart: CartItem[]
  cartOwnerHash: string | null
  recoveryCartHash: string | null
  /** Set when a product with an upsell modal was just added; the cart page consumes it to open the modal. Not persisted. */
  upsellPending: UpsellTrigger | null
  setUpsellPending: (trigger: UpsellTrigger | null) => void
  /** Product hashes the host page injected into the cart since the cart page last read them. Not persisted. */
  injectedProducts: string[]
  takeInjectedProducts: () => string[]
  cart_hash: string
  reorderHash: string | null
  /** Delivery method of the reordered order; the cart checkout selects it once while that reorder is active, if still offered. */
  reorderShippingMethodId: string | null
  setReorderShippingMethodId: (methodId: string | null) => void
  /** Order whose addresses were last copied into checkout, so they are copied once. */
  reorderPrefilledFor: string | null
  setReorderPrefilledFor: (orderHash: string | null) => void
  lastCouponCartLength: number
  getCartTotal: () => number
  hasSubscribtion: () => boolean
  shippingType: ShippingType
  shippingAddress: RegisterForm['shipping']
  billingAddress: RegisterForm['billing']
  billingSame: boolean
  email: string
  /** What the WhatsApp toggle shows, and so what checkout saves; null until their saved choice loads. Not persisted, so a stale one can't undo a later opt-out. */
  whatsAppOptIn: boolean | null
  setWhatsAppOptIn: (whatsAppOptIn: boolean) => void
  /** Starts the toggle from their saved choice, unless it already shows one */
  initWhatsAppOptIn: (saved: boolean) => void
  setShippingType: (shippingType: ShippingType) => void
  setShippingAddress: (shippingAddress: RegisterForm['shipping']) => void
  setBillingAddress: (billingAddress: RegisterForm['billing']) => void
  setBillingSame: (billingSame: boolean) => void
  setReorderHash: (reorderHash: string | null) => void
  addToCart: (cartItem: CartItem) => Promise<boolean>
  removeFromCart: (cartItem: CartItem) => void
  /** Swap an item for another variation of the same product, in the same place in the cart. False when the swap is refused. */
  replaceCartItem: (current: CartItem, next: CartItem) => Promise<boolean>
  removeCartItems: (items: CartItem[]) => Promise<void>
  removeReorderItems: (reorderHash: string) => Promise<void>
  clearReorderTags: (reorderHash: string) => void
  reconcileUnverifiedItems: () => Promise<void>
  /** Swaps items sent with a parent product hash for their variation, or removes them with a message. Checks `items`, or the whole cart. */
  fixParentHashItems: (items?: CartItem[]) => Promise<{ replaced: CartItem[]; removed: CartItem[] }>
  syncCart: (id?: string) => void
  enrichCart: () => Promise<void>
  setEmail: (email: string) => void
  reset: () => void
}

const initialState: Pick<
  CheckoutStore,
  | 'recoveryCartHash'
  | 'upsellPending'
  | 'injectedProducts'
  | 'shippingType'
  | 'cart'
  | 'cartOwnerHash'
  | 'cart_hash'
  | 'shippingAddress'
  | 'billingAddress'
  | 'billingSame'
  | 'email'
  | 'whatsAppOptIn'
  | 'coupon'
  | 'rated'
  | 'reorderHash'
  | 'reorderShippingMethodId'
  | 'reorderPrefilledFor'
  | 'lastCouponCartLength'
> = {
  shippingType: null,
  cart: [],
  cartOwnerHash: null,
  cart_hash: null,
  recoveryCartHash: null,
  upsellPending: null,
  injectedProducts: [],
  shippingAddress: null,
  billingAddress: null,
  billingSame: true,
  email: '',
  whatsAppOptIn: null,
  coupon: null,
  rated: [],
  reorderHash: null,
  reorderShippingMethodId: null,
  reorderPrefilledFor: null,
  lastCouponCartLength: 0
}

const cartItemKey = (item: CartItem) => `${item.id}|${item.patient ?? ''}`

const DUPLICATE_ITEM_MESSAGE = 'This item is already in your cart for this patient'

let enrichCartInFlight: Promise<void> | null = null

const scheduleCartEnrichment: () => void = () => {
  setTimeout(() => {
    void useCheckoutStore.getState().enrichCart()
  }, 0)
}

export const useCheckoutStore = create<CheckoutStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      setCoupon: coupon => {
        set({ coupon })
        set({ lastCouponCartLength: get().cart.length })
      },
      revalidateCoupon: async () => {
        const coupon = get().coupon
        if (!coupon) return

        try {
          const response = await http.post<CouponData, CouponData>('/coupons/validate', {
            coupon_code: coupon.code,
            cart: get().cart,
            // Match the apply path for referral coupons, whose call sites send false.
            subscriptionCheck: coupon.hidden ? false : get().hasSubscribtion()
          })

          // A code applied while this request was in flight is newer, so keep it.
          if (get().coupon !== coupon) return
          // If validation succeeds, update the coupon with the response data.
          // Preserve the existing value if the API response doesn't include one,
          // since value is now optional on CouponData.
          set({
            coupon: { ...response, code: coupon.code, value: response.value ?? coupon.value, hidden: coupon.hidden },
            lastCouponCartLength: get().cart.length
          })
        } catch (error) {
          // Runs on every token refresh, so a network fault or server error keeps the coupon.
          // The cart page can re-apply the same code for a newer cart while this request is in flight.
          if (!isCouponRefusal(error) || get().coupon !== coupon) return
          set({ coupon: null, lastCouponCartLength: get().cart.length })
          // A referral coupon is cleared silently; the cart page re-checks its stored code on the next cart change.
          if (!coupon.hidden) toast.error('The applied coupon is no longer valid and has been removed.')
        }
      },
      getCartTotal: () => {
        return get().cart.reduce((a, b) => a + b.price, 0)
      },
      hasSubscribtion: () => cartHasSubscription(get().cart),
      setShippingType: shippingType => set({ shippingType }),
      setShippingAddress: shippingAddress => set({ shippingAddress }),
      setBillingAddress: billingAddress => set({ billingAddress }),
      setBillingSame: billingSame => set({ billingSame }),
      setReorderHash: (reorderHash: string | null) => set({ reorderHash }),
      setReorderShippingMethodId: reorderShippingMethodId => set({ reorderShippingMethodId }),
      setReorderPrefilledFor: reorderPrefilledFor => set({ reorderPrefilledFor }),
      setUpsellPending: upsellPending => set({ upsellPending }),
      takeInjectedProducts: () => {
        const injected = get().injectedProducts
        if (injected.length) set({ injectedProducts: [] })
        return injected
      },
      addToCart: async cartItem => {
        const currentCart = get().cart
        // Check if item with same id and patient already exists
        const existingItemIndex = currentCart.findIndex(item => item.id === cartItem.id && item.patient === cartItem.patient)

        if (existingItemIndex !== -1) {
          // Item already exists for this patient, don't add it
          toast.warning(DUPLICATE_ITEM_MESSAGE)
          return false
        }

        logAddToCart(cartItem)
        const data = [...currentCart, { ...cartItem, addLogged: true }]
        set({ cart: data })
        if (useAuthState.getState().isLogged) {
          const { cart_hash } = await createCart(data)
          set({
            cart_hash: cart_hash,
            cartOwnerHash: useAuthState.getState().userHash
          })
        }
        return true
      },
      removeFromCart: async cartItem => {
        logRemoveFromCart(cartItem)
        // Same product can sit in the cart once per patient, so the key has to include the patient.
        const i = get().cart.findIndex(item => cartItemKey(item) === cartItemKey(cartItem))
        const data = get().cart.filter((item, index) => index !== i)
        set({ cart: data })
        if (useAuthState.getState().isLogged) {
          const { cart_hash } = await createCart(data)
          set({
            cart_hash: cart_hash,
            cartOwnerHash: useAuthState.getState().userHash
          })
        }
        if (get().cart.length === 0) {
          set({ coupon: null })
        }
      },
      replaceCartItem: async (current, next) => {
        const i = get().cart.findIndex(item => cartItemKey(item) === cartItemKey(current))
        if (i === -1 || current.id === next.id) return false
        if (get().cart.some(item => cartItemKey(item) === cartItemKey(next))) {
          toast.warning(DUPLICATE_ITEM_MESSAGE)
          return false
        }
        const others = get().cart.filter((_, index) => index !== i)
        const conflict = getMixedCartConflict({
          cartLength: others.length,
          hasSubscriptionInCart: cartHasSubscription(others),
          hasInvestigationInCart: cartHasInvestigation(others),
          hasMultiProductInCart: cartHasMultiProduct(others),
          isAddingSubscription: cartHasSubscription([next]),
          isAddingInvestigation: cartHasInvestigation([next])
        })
        if (conflict) {
          toast.warning(getMixedCartMessage(conflict))
          return false
        }
        logRemoveFromCart(current)
        logAddToCart(next)
        const data = get().cart.map((item, index) => (index === i ? { ...next, addLogged: true } : item))
        set({ cart: data })
        if (useAuthState.getState().isLogged) {
          const { cart_hash } = await createCart(data)
          set({ cart_hash, cartOwnerHash: useAuthState.getState().userHash })
        }
        // The cart length does not change, so the coupon page would not re-check it.
        await get().revalidateCoupon()
        return true
      },
      removeReorderItems: async reorderHash => {
        const data = get().cart.filter(item => item.reorderHash !== reorderHash)
        if (data.length === get().cart.length) return
        set({ cart: data })
        if (useAuthState.getState().isLogged) {
          const { cart_hash } = await createCart(data)
          set({ cart_hash, cartOwnerHash: useAuthState.getState().userHash })
        }
        if (data.length === 0) {
          set({ coupon: null })
        }
      },
      clearReorderTags: reorderHash => {
        if (!get().cart.some(item => item.reorderHash === reorderHash)) return
        set({ cart: get().cart.map(item => (item.reorderHash === reorderHash ? { ...item, reorderHash: undefined } : item)) })
      },
      removeCartItems: async items => {
        const keys = new Set(items.map(cartItemKey))
        const data = get().cart.filter(item => !keys.has(cartItemKey(item)))
        if (data.length === get().cart.length) return
        items.forEach(logRemoveFromCart)
        set({ cart: data })
        if (useAuthState.getState().isLogged) {
          const { cart_hash } = await createCart(data)
          set({ cart_hash, cartOwnerHash: useAuthState.getState().userHash })
        }
        if (data.length === 0) {
          set({ coupon: null })
        }
      },
      reconcileUnverifiedItems: async () => {
        const isUnverified = (item: CartItem) => !!item.recoveryCartHash || !!item.reorderHash
        if (!get().cart.some(isUnverified) || !useAuthState.getState().isLogged) return
        let ownPatients: Set<string>
        try {
          ownPatients = new Set((await getPatientsList()).map(patient => patient.hash))
        } catch (e) {
          // Leave the tags in place so the next sync retries.
          console.warn('Error checking cart items loaded from a public link', e)
          return
        }
        // An item loaded from a public reorder or recovery payload carries whatever patient that
        // order or cart was built for. Keep it only when that patient is the logged-in user's;
        // otherwise drop patient and answer so the checkout guard sends the user through the
        // consultation again.
        const cart = get().cart.map(item => {
          if (!isUnverified(item)) return item
          const { recoveryCartHash, reorderHash, ...rest } = item
          return rest.patient && !ownPatients.has(rest.patient) ? { ...rest, patient: undefined, answer: undefined } : rest
        })
        // Persist first: clearing the tags before the write lands would strand a foreign patient in
        // the server cart with nothing left to retry from.
        try {
          const { cart_hash } = await createCart(cart)
          set({ cart, cart_hash, cartOwnerHash: useAuthState.getState().userHash })
        } catch (e) {
          console.warn('Error saving the cart after checking items loaded from a public link', e)
        }
      },
      fixParentHashItems: async items => {
        const changes = await resolveParentHashItems(items ?? get().cart)
        const fixes = new Map(changes.map(change => [cartItemKey(change.from), change.to]))
        // Re-read the cart so items added during the lookup stay.
        const cart = get().cart
        const stale = cart.filter(item => fixes.has(cartItemKey(item)))
        if (!stale.length) return { replaced: [], removed: [] }

        const replaced = stale.map(item => fixes.get(cartItemKey(item))).filter((to): to is CartItem => !!to)
        const removed = stale.filter(item => !fixes.get(cartItemKey(item)))
        // The variation can already be in the cart for this patient; keep just that one.
        const keys = new Set(cart.filter(item => !fixes.has(cartItemKey(item))).map(cartItemKey))
        const next = cart.flatMap(item => {
          if (!fixes.has(cartItemKey(item))) return [item]
          const to = fixes.get(cartItemKey(item))
          if (!to || keys.has(cartItemKey(to))) return []
          keys.add(cartItemKey(to))
          return [to]
        })

        set({ cart: next, ...(next.length ? {} : { coupon: null }) })
        removed.filter(item => item.addLogged).forEach(logRemoveFromCart)
        for (const item of removed) {
          toast.info(`We couldn't add ${item.title} to your cart. Please choose an option on its product page.`)
        }

        if (useAuthState.getState().isLogged) {
          try {
            const { cart_hash } = await createCart(next)
            set({ cart_hash, cartOwnerHash: useAuthState.getState().userHash })
          } catch (e) {
            console.warn('Error saving the cart after fixing items sent with a parent product hash', e)
          }
        }
        return { replaced, removed }
      },
      enrichCart: () => {
        // Coalesce concurrent callers (rehydrate hook + pages) onto one run.
        if (enrichCartInFlight) return enrichCartInFlight
        const seen = new Set<string>()
        const run = async () => {
          // Items the host page injected can carry a parent product hash; fix them before they're logged.
          await get().fixParentHashItems(get().cart.filter(needsEnrichment))
          const cart = get().cart
          for (const item of cart) seen.add(cartItemKey(item))
          if (!cart.some(needsEnrichment)) return

          // Items without categories and without an add_to_cart on record were
          // injected from the parent page (host add-to-cart) — log them once.
          const toLog = new Set(cart.filter(item => needsEnrichment(item) && !item.addLogged).map(cartItemKey))
          const enriched = await enrichCartItemsWithCategories(cart)
          const enrichedByKey = new Map(enriched.map(item => [cartItemKey(item), toLog.has(cartItemKey(item)) ? { ...item, addLogged: true } : item]))

          enrichedByKey.forEach((item, key) => {
            if (toLog.has(key)) logAddToCart(item)
          })

          const injected = enriched.filter(item => toLog.has(cartItemKey(item))).map(item => item.parentId)

          // Re-read the cart so items added while enriching aren't dropped.
          set({
            cart: get().cart.map(item => enrichedByKey.get(cartItemKey(item)) ?? item),
            ...(injected.length ? { injectedProducts: [...get().injectedProducts, ...injected] } : {})
          })
        }
        // Cleared in finally() so a run that returns synchronously can't leave its settled promise behind.
        enrichCartInFlight = run().finally(() => {
          enrichCartInFlight = null
          // Items the host page injected while this run was in flight still need their pass.
          if (get().cart.some(item => needsEnrichment(item) && !item.addLogged && !seen.has(cartItemKey(item)))) scheduleCartEnrichment()
        })
        return enrichCartInFlight
      },
      syncCart: async (id?: string) => {
        const localItems = get().cart
        // A browser cart not yet saved to this account (a guest cart at login) is newer than the saved one.
        const localIsNewer = get().cartOwnerHash !== useAuthState.getState().userHash

        try {
          // biome-ignore lint/style/noVar: <explanation>
          // biome-ignore lint/correctness/noInnerDeclarations: <explanation>
          var { items, cart_hash } = id
            ? await getCartByHash(id)
                .then(res => {
                  logRecoveredCartSuccess()
                  return res
                })
                .catch(() => {
                  logRecoveredCartFailure()
                  toast.error('Oops! Sorry there was an error loading your cart, please try again later.')
                  return { items: [] as CartItem[], cart_hash: null as string | null }
                })
            : await getCurrentCart()
        } catch (e) {
          console.warn('Error syncing or getting cart', e)
          return
        }

        if (!cart_hash) {
          // No server cart to merge — still enrich the local items so GA4 events carry categories.
          await get().enrichCart()
          return
        }

        // Items loaded through a recovery link keep the link's hash until their patient is checked against the logged-in user.
        const incomingItems = id ? items.map(item => ({ ...item, recoveryCartHash: id })) : items
        const allItems = [...localItems, ...incomingItems]
        // Deduplicate by item id and patient - keep the first occurrence of each unique combination
        const uniqueItems = allItems.filter((item, index) => allItems.findIndex(i => i.id === item.id && i.patient === item.patient) === index)
        const enrichedItems = await enrichCartItemsWithCategories(uniqueItems)

        const localKeys = new Set(localItems.map(cartItemKey))
        const incomingKeys = new Set(incomingItems.map(cartItemKey))
        // A recovery link, or a saved cart once the browser cart belongs to the account, is newer than the browser's cart.
        const newerKeys = !id && localIsNewer ? localKeys : incomingKeys
        const resolvedItems = resolveSingleItemCart(enrichedItems, item => newerKeys.has(cartItemKey(item)))
        const droppedItems = enrichedItems.filter(item => !resolvedItems.includes(item))
        const isEqual = JSON.stringify(items) === JSON.stringify(resolvedItems)
        const { isLogged, userHash } = useAuthState.getState()
        const needsSave = (!!localItems.length || !!droppedItems.length) && !isEqual && isLogged

        // Server items had their add_to_cart logged when first added, so enrichment must not treat them as host-injected.
        // The cart only belongs to the account once the merged result is saved.
        set({
          cart: resolvedItems.map(item => (incomingKeys.has(cartItemKey(item)) && !localKeys.has(cartItemKey(item)) ? { ...item, addLogged: true } : item)),
          cart_hash: cart_hash,
          cartOwnerHash: isLogged && !needsSave ? userHash : get().cartOwnerHash
        })

        // Saved items dropped by the trim are reported once the save lands, so an unsaved trim can't report them twice.
        const isLocal = (item: CartItem) => localKeys.has(cartItemKey(item))
        if (droppedItems.length) {
          droppedItems.filter(isLocal).forEach(logRemoveFromCart)
          toast.info(SINGLE_ITEM_CART_TRIMMED_MESSAGE)
        }

        if (needsSave) {
          const { cart_hash } = await createCart(resolvedItems)
          set({
            cart_hash: cart_hash,
            cartOwnerHash: userHash
          })
          droppedItems.filter(item => !isLocal(item)).forEach(logRemoveFromCart)
        }

        await get().reconcileUnverifiedItems()
      },
      setEmail: email => set({ email }),
      setWhatsAppOptIn: whatsAppOptIn => set({ whatsAppOptIn }),
      initWhatsAppOptIn: saved => set(state => ({ whatsAppOptIn: state.whatsAppOptIn ?? saved })),
      setRated: orderHash => set({ rated: [...get().rated, orderHash] }),
      reset: () =>
        set({
          ...initialState,
          rated: get().rated,
          reorderHash: get().reorderHash // Preserve reorderHash through reset
        })
    }),
    {
      name: 'checkout_state',
      storage: createJSONStorage(() => safeStorage),
      version: 3,
      skipHydration: true,
      partialize: ({ upsellPending: _upsellPending, injectedProducts: _injectedProducts, whatsAppOptIn: _whatsAppOptIn, ...state }) => ({
        ...state,
        recoveryCartHash: null as string | null
      }),
      onRehydrateStorage: () => state => {
        // Normalize old phone format (0...) to international format (+44...) on load
        if (state?.shippingAddress?.phone?.startsWith('0')) {
          state.shippingAddress.phone = `+44${state.shippingAddress.phone.slice(1)}`
        }
        // Items injected from the parent page (host add-to-cart) arrive via rehydrate
        // without categories — enrich them and log their add_to_cart.
        if (state?.cart?.length) {
          scheduleCartEnrichment()
        }
      }
    }
  )
)
